import {useEffect} from "react";
import {StatsApi} from "../api/StatsApi";
import {SiteStatHit} from "../api/models";
import {useAuth} from "../auth/AuthContext";
import {useSiteStatus} from "./SiteStatusContext";

// The site's anonymous visit counts: which pages, products and checkouts people open, and where visits come from. The
// API keeps only daily totals by country (tristar-corp-api Models/stats/SiteStat.ts): nothing is stored in the browser
// and nothing identifies anyone. See the privacy policy's "Anonymous statistics".
//
// Nothing is reported when the stage doesn't count visits (stages/*.yml analytics), for administrators (their own
// browsing), in maintenance mode, or from automated browsers (scripts/prerender.mjs's Playwright, tests).
export const statsSettings = { enabled: ANALYTICS_ENABLED };

// Every page the site has, as the API counts them (tristar-corp-api SITE_STAT_PAGES, which must list the same): one
// each, by its route, so a product's, a ticket's or a person's id is never sent. A fixed segment wins over a :parameter
// (/docs/glossary before /docs/:sku), so keep those first. Add a page here and to the API with each new route.
export const STATS_PAGES: string[] = [
    '/', '/products', '/products/:sku', '/products/:sku/download', '/docs', '/docs/glossary', '/docs/:sku',
    '/terms', '/privacy', '/refunds', '/cookies', '/accessibility', '/ai-policy',
    '/contact', '/press', '/press/contact',
    '/login', '/forgot-password', '/reset-password', '/verify-email', '/news/confirm', '/news/unsubscribe',
    '/checkout/pay', '/checkout/complete', '/checkout/:sku',
    '/support', '/support/new', '/support/:ticketId',
    '/account/profile', '/account/licenses', '/account/orders'
];

// Any address that isn't one of the site's pages: what the not-found page is counted as.
export const NOT_FOUND_PAGE = '(not found)';

// The page an address is (see STATS_PAGES). /account is the profile it opens, and the administration area is one page
// (administrators' own browsing isn't counted, so it's only someone turned away from it).
export function statsPage(pathname: string): string {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    if (path === '/account') return '/account/profile';
    if (path === '/admin' || path.startsWith('/admin/')) return '/admin';
    const segments = path.split('/');
    const page = STATS_PAGES.find(candidate => {
        const parts = candidate.split('/');
        return parts.length === segments.length && parts.every((part, i) => part.startsWith(':') ? segments[i] !== '' : part === segments[i]);
    });
    return page ?? NOT_FOUND_PAGE;
}

// Coming back from paying (or not) isn't a new visit.
const PAYMENT_HOSTS = /(^|\.)(stripe\.com|paypal\.com|paddle\.io|paddle\.com)$/i;

// A visit's first page: where it came from (the referring page without its query, which can hold personal details) and
// the landing address's campaign tags. Undefined when it's a return from Stripe, PayPal or Paddle.
export function entryHit(location: { pathname: string, search: string }, referrer: string): SiteStatHit | undefined {
    let from = '';
    if (referrer) {
        try {
            const url = new URL(referrer);
            if (PAYMENT_HOSTS.test(url.hostname)) {
                return undefined;
            }
            from = `${url.origin}${url.pathname}`;
        } catch {
            // Not a URL: direct.
        }
    }
    const params = new URLSearchParams(location.search);
    const utm = { source: params.get('utm_source') ?? undefined, medium: params.get('utm_medium') ?? undefined, campaign: params.get('utm_campaign') ?? undefined };
    return {
        event: 'entry',
        path: statsPage(location.pathname),
        ...(from && { referrer: from }),
        ...(utm.source && { utm })
    };
}

function canReport(): boolean {
    return statsSettings.enabled && !navigator.webdriver;
}

// Reports hit once it's known who's browsing (not an administrator) and that the site isn't down for maintenance, and
// again whenever it or occasion changes (the address, for page views: two products' pages are the same page).
// Undefined: nothing to report yet.
export function useSiteStatsHit(hit: SiteStatHit | undefined, occasion?: string): void {
    const {loading, isAdmin} = useAuth();
    const {maintenance, checked} = useSiteStatus();
    const key = hit && JSON.stringify([hit, occasion]);
    const ready = !loading && checked;
    useEffect(() => {
        if (key && ready && !isAdmin && !maintenance && canReport()) {
            void StatsApi.hit(JSON.parse(key)[0]).catch(() => undefined);
        }
        // Once per hit: not again when the session or maintenance mode changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, ready]);
}

let entryReported = false;

// The visit's first page, once per page load (a new tab or a reload is a new visit).
export function useEntryHit(location: { pathname: string, search: string }): void {
    const {loading, isAdmin} = useAuth();
    const {maintenance, checked} = useSiteStatus();
    const ready = !loading && checked;
    useEffect(() => {
        if (!ready || entryReported) {
            return;
        }
        entryReported = true;
        const hit = entryHit(location, document.referrer);
        if (hit && !isAdmin && !maintenance && canReport()) {
            void StatsApi.hit(hit).catch(() => undefined);
        }
        // The first page only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready]);
}

// For tests: as a new page load.
export function resetEntryHit(): void {
    entryReported = false;
}
